import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';

/**
 * Check if the app is running on a native platform (iOS/Android)
 * Returns false for web/PWA
 */
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Get the current platform ('ios', 'android', or 'web')
 */
export const getPlatform = (): string => {
  return Capacitor.getPlatform();
};

type HapticType = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

/**
 * Trigger haptic feedback on native platforms
 * Does nothing on web - safe to call anywhere
 */
export const triggerHaptic = async (type: HapticType = 'light'): Promise<void> => {
  // Only run on native platforms
  if (!isNativePlatform()) {
    return;
  }

  try {
    switch (type) {
      case 'light':
        await Haptics.impact({ style: ImpactStyle.Light });
        break;
      case 'medium':
        await Haptics.impact({ style: ImpactStyle.Medium });
        break;
      case 'heavy':
        await Haptics.impact({ style: ImpactStyle.Heavy });
        break;
      case 'success':
        await Haptics.notification({ type: NotificationType.Success });
        break;
      case 'warning':
        await Haptics.notification({ type: NotificationType.Warning });
        break;
      case 'error':
        await Haptics.notification({ type: NotificationType.Error });
        break;
    }
  } catch (error) {
    // Silently fail on web or if haptics not available
    console.warn('[Capacitor] Haptics not available:', error);
  }
};

/**
 * Open the native camera to take a photo
 * Throws an error if not on native platform - use isNativePlatform() to check first
 */
export const openNativeCamera = async (): Promise<string> => {
  if (!isNativePlatform()) {
    throw new Error('Native camera is only available on iOS/Android');
  }

  const image = await Camera.getPhoto({
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
  });

  if (!image.webPath) {
    throw new Error('Failed to capture image');
  }

  return image.webPath;
};

/**
 * Open the native photo gallery to select an image
 * Throws an error if not on native platform
 */
export const openNativeGallery = async (): Promise<string> => {
  if (!isNativePlatform()) {
    throw new Error('Native gallery is only available on iOS/Android');
  }

  const image = await Camera.getPhoto({
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Photos,
  });

  if (!image.webPath) {
    throw new Error('Failed to select image');
  }

  return image.webPath;
};
